import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import { teamService } from '../services';
import { Asset, TeamRequest, TeamInvitation, TeamMemberInfo, TeamOverview, TeamStatus } from '../types';
import { useAuthStore } from '../store';
import PageTopBar from '../components/ui/PageTopBar';
import Card, { CardHeader, CardBody } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import ConfirmationModal from '../components/common/ConfirmationModal';

// Icons
const UsersIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
    />
  </svg>
);

const InboxIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
    />
  </svg>
);

const formatRelativeDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return date.toLocaleDateString();
};

const Team: React.FC = () => {
  const { user } = useAuthStore();
  const [overview, setOverview] = useState<TeamOverview | null>(null);
  const [pendingRequests, setPendingRequests] = useState<TeamRequest[]>([]);
  const [teamStatus, setTeamStatus] = useState<TeamStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [showResponseModal, setShowResponseModal] = useState<{
    request: TeamRequest;
    action: 'approve' | 'reject';
  } | null>(null);
  const [removingMember, setRemovingMember] = useState<{ id: string; name: string } | null>(null);
  const [expandedCoverage, setExpandedCoverage] = useState<string | null>(null);
  const [memberCoverage, setMemberCoverage] = useState<Asset[]>([]);
  const [loadingCoverage, setLoadingCoverage] = useState(false);

  // Invitation state
  const [sentInvitations, setSentInvitations] = useState<TeamInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const teamMembers = overview?.members || [];

  // Sort state (manager is always pinned at top, sort only applies to workers)
  type TeamSortField = 'member' | 'coverage' | 'tasks';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<TeamSortField>('member');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: TeamSortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedMembers = useMemo(() => {
    const sorted = [...teamMembers];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'member':
          cmp = a.fullName.localeCompare(b.fullName);
          break;
        case 'coverage':
          cmp = a.coverageCount - b.coverageCount;
          break;
        case 'tasks':
          cmp = a.activeAssignments - b.activeAssignments;
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [teamMembers, sortField, sortDir]);

  const SortHeader: React.FC<{ field: TeamSortField; label: string; align?: string }> = ({ field, label, align }) => (
    <th
      className={`px-6 py-3 text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer select-none hover:text-neutral-700 transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => toggleSort(field)}
    >
      <span className={`inline-flex items-center gap-1.5 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        <FontAwesomeIcon
          icon={sortField === field ? (sortDir === 'asc' ? faSortUp : faSortDown) : faSort}
          className={`w-3 h-3 ${sortField === field ? 'text-primary-500' : 'text-neutral-300'}`}
        />
      </span>
    </th>
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [overviewData, requestsData, statusData, invitationsData] = await Promise.all([
        teamService.getTeamOverview(),
        teamService.getReceivedRequests('pending'),
        teamService.getTeamStatus(),
        teamService.getSentInvitations(),
      ]);
      setOverview(overviewData);
      setPendingRequests(requestsData);
      setTeamStatus(statusData);
      setSentInvitations(invitationsData);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCoverage = async (memberId: string) => {
    if (expandedCoverage === memberId) {
      setExpandedCoverage(null);
      setMemberCoverage([]);
      return;
    }
    try {
      setLoadingCoverage(true);
      setExpandedCoverage(memberId);
      const assets = await teamService.getMemberCoverage(memberId);
      setMemberCoverage(assets);
    } catch {
      setMemberCoverage([]);
    } finally {
      setLoadingCoverage(false);
    }
  };

  const handleRespondToRequest = async (approved: boolean) => {
    if (!showResponseModal) return;

    try {
      setProcessingId(showResponseModal.request.id);
      await teamService.respondToRequest(showResponseModal.request.id, {
        approved,
        responseMessage: responseMessage || undefined,
      });
      setShowResponseModal(null);
      setResponseMessage('');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to process request');
    } finally {
      setProcessingId(null);
    }
  };

  const confirmRemoveMember = async () => {
    if (!removingMember) return;

    try {
      setProcessingId(removingMember.id);
      await teamService.removeTeamMember(removingMember.id);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove team member');
    } finally {
      setProcessingId(null);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    try {
      setInviteSubmitting(true);
      setInviteError(null);
      await teamService.createInvitation({
        email: inviteEmail.trim(),
        message: inviteMessage || undefined,
      });
      setInviteEmail('');
      setInviteMessage('');
      await loadData();
    } catch (err: any) {
      setInviteError(err.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await teamService.cancelInvitation(invitationId);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to cancel invitation');
    }
  };

  if (loading) {
    return (
      <div className="h-full bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-neutral-50">
      <PageTopBar
        title="Team Management"
        description="Manage your team members and join requests"
      />
      <div className="flex-1 overflow-y-auto">
      <div className="w-full px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-danger-500 hover:text-danger-700 font-medium"
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left column: Invite + Pending Requests */}
          <div className="lg:col-span-1 space-y-6">
            {/* Invite User */}
            <Card variant="default">
              <CardHeader>
                <div>
                  <h2 className="text-base font-semibold text-neutral-900">Invite User</h2>
                  <p className="text-xs text-neutral-500">Invite someone by email to join your team</p>
                </div>
              </CardHeader>
              <CardBody>
                <form onSubmit={handleInvite} className="space-y-3">
                  <div>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={e => setInviteEmail(e.target.value)}
                      placeholder="user@example.com"
                      required
                      className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg transition-all"
                    />
                  </div>
                  <div>
                    <textarea
                      value={inviteMessage}
                      onChange={e => setInviteMessage(e.target.value)}
                      placeholder="Add a message (optional)"
                      rows={2}
                      maxLength={500}
                      className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg resize-none transition-all"
                    />
                  </div>
                  {inviteError && (
                    <p className="text-sm text-danger-600">{inviteError}</p>
                  )}
                  <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={inviteSubmitting || !inviteEmail.trim()}
                    className="w-full"
                  >
                    {inviteSubmitting ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </form>

                {/* Sent invitations */}
                {sentInvitations.filter(i => i.status === 'pending').length > 0 && (
                  <div className="mt-4 pt-4 border-t border-neutral-100">
                    <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-2">
                      Pending Invitations
                    </p>
                    <div className="space-y-2">
                      {sentInvitations
                        .filter(i => i.status === 'pending')
                        .map(inv => (
                          <div key={inv.id} className="flex items-center justify-between py-2">
                            <div>
                              <p className="text-sm font-medium text-neutral-900">{inv.email}</p>
                              <p className="text-xs text-neutral-400">
                                {formatRelativeDate(inv.createdAt)}
                              </p>
                            </div>
                            <button
                              onClick={() => handleCancelInvitation(inv.id)}
                              className="text-xs text-danger-500 hover:text-danger-700 font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            <Card variant="default">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-warning-100 rounded-lg flex items-center justify-center text-warning-600">
                    <InboxIcon />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-neutral-900 flex items-center gap-2">
                      Join Requests
                      {pendingRequests.length > 0 && (
                        <Badge variant="warning" size="sm">
                          {pendingRequests.length}
                        </Badge>
                      )}
                    </h2>
                    <p className="text-xs text-neutral-500">Workers requesting to join</p>
                  </div>
                </div>
              </CardHeader>
              <div className="divide-y divide-neutral-100">
                {pendingRequests.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mx-auto mb-3 text-neutral-400">
                      <InboxIcon />
                    </div>
                    <p className="text-sm text-neutral-500">No pending requests</p>
                  </div>
                ) : (
                  pendingRequests.map(request => (
                    <div key={request.id} className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-sm font-semibold text-primary-600">
                              {request.workerName?.charAt(0) || '?'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-neutral-900">{request.workerName}</p>
                            <p className="text-xs text-neutral-500">{request.workerEmail}</p>
                          </div>
                        </div>
                        <span className="text-xs text-neutral-400">
                          {formatRelativeDate(request.createdAt)}
                        </span>
                      </div>
                      {request.message && (
                        <p className="text-sm text-neutral-600 bg-neutral-50 rounded-lg p-3 mb-3 italic border border-neutral-100">
                          "{request.message}"
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => setShowResponseModal({ request, action: 'approve' })}
                          disabled={processingId === request.id}
                          className="flex-1"
                        >
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowResponseModal({ request, action: 'reject' })}
                          disabled={processingId === request.id}
                          className="flex-1 text-danger-600 border-danger-200 hover:bg-danger-50"
                        >
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Team Members (unified table: manager + workers) */}
          <div className="lg:col-span-2">
            <Card variant="default">
              <div className="overflow-x-auto no-scrollbar">
                <table className="w-full">
                  <thead>
                    <tr className="bg-neutral-50 border-y border-neutral-100">
                      <SortHeader field="member" label="Member" />
                      <SortHeader field="coverage" label="Coverage" />
                      <SortHeader field="tasks" label="Active Tasks" />
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {/* Manager row — pinned at top, visually distinct */}
                    {overview?.manager && (
                      <React.Fragment>
                        <tr className="bg-primary-900/30 border-l-4 border-l-primary-500">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <p className="font-semibold text-primary-400">
                                {overview.manager.fullName}
                                <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-primary-500">Manager</span>
                              </p>
                              <p className="text-sm text-neutral-500">{overview.manager.email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleCoverage(overview.manager.id)}
                              className={`text-sm font-medium transition-colors ${
                                expandedCoverage === overview.manager.id
                                  ? 'text-primary-600'
                                  : 'text-neutral-700 hover:text-primary-600'
                              }`}
                            >
                              {overview.manager.coverageCount > 0
                                ? `${overview.manager.coverageCount} assets`
                                : 'All assets'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge
                              variant={overview.manager.activeAssignments > 0 ? 'primary' : 'secondary'}
                              size="sm"
                            >
                              {overview.manager.activeAssignments} tasks
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right"></td>
                        </tr>
                        {expandedCoverage === overview.manager.id && (
                          <tr>
                            <td colSpan={4} className="px-6 py-3 bg-neutral-50">
                              {loadingCoverage ? (
                                <p className="text-sm text-neutral-500">Loading coverage...</p>
                              ) : memberCoverage.length === 0 ? (
                                <p className="text-sm text-neutral-500">No specific coverage — can work on all assets</p>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  {memberCoverage.map(asset => (
                                    <span key={asset.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-sm text-neutral-700 rounded-lg border border-neutral-200">
                                      {asset.name}
                                      {asset.dataUid && <span className="text-neutral-400 text-xs">({asset.dataUid})</span>}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )}
                    {/* Worker rows */}
                    {sortedMembers.map(member => (
                      <React.Fragment key={member.id}>
                        <tr className="hover:bg-neutral-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <p className="font-medium text-neutral-900">{member.fullName}</p>
                              <p className="text-sm text-neutral-500">{member.email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleCoverage(member.id)}
                              className={`text-sm font-medium transition-colors ${
                                expandedCoverage === member.id
                                  ? 'text-primary-600'
                                  : 'text-neutral-700 hover:text-primary-600'
                              }`}
                            >
                              {member.coverageCount > 0
                                ? `${member.coverageCount} assets`
                                : 'All assets'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge
                              variant={member.activeAssignments > 0 ? 'primary' : 'secondary'}
                              size="sm"
                            >
                              {member.activeAssignments} tasks
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() =>
                                setRemovingMember({ id: member.id, name: member.fullName })
                              }
                              disabled={processingId === member.id}
                              className="px-3 py-1.5 text-xs font-medium text-danger-600 border border-danger-300 rounded shadow-sm hover:bg-danger-50 transition-colors disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                        {expandedCoverage === member.id && (
                          <tr>
                            <td colSpan={4} className="px-6 py-3 bg-neutral-50">
                              {loadingCoverage ? (
                                <p className="text-sm text-neutral-500">Loading coverage...</p>
                              ) : memberCoverage.length === 0 ? (
                                <p className="text-sm text-neutral-500">No specific coverage — can work on all assets</p>
                              ) : (
                                <div className="flex flex-wrap gap-2">
                                  {memberCoverage.map(asset => (
                                    <span key={asset.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-sm text-neutral-700 rounded-lg border border-neutral-200">
                                      {asset.name}
                                      {asset.dataUid && <span className="text-neutral-400 text-xs">({asset.dataUid})</span>}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>
      </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-black/70 transition-opacity"
              onClick={() => setShowResponseModal(null)}
            />
            <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                {showResponseModal.action === 'approve' ? 'Approve' : 'Reject'} Request
              </h3>
              <p className="text-sm text-neutral-600 mb-4">
                {showResponseModal.action === 'approve'
                  ? `Add ${showResponseModal.request.workerName} to your team?`
                  : `Reject the request from ${showResponseModal.request.workerName}?`}
              </p>
              <div className="mb-6">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Response message (optional)
                </label>
                <textarea
                  value={responseMessage}
                  onChange={e => setResponseMessage(e.target.value)}
                  placeholder={
                    showResponseModal.action === 'approve'
                      ? 'Welcome to the team!'
                      : 'Reason for rejection...'
                  }
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg resize-none transition-all"
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowResponseModal(null);
                    setResponseMessage('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant={showResponseModal.action === 'approve' ? 'success' : 'danger'}
                  onClick={() => handleRespondToRequest(showResponseModal.action === 'approve')}
                  disabled={processingId === showResponseModal.request.id}
                >
                  {processingId === showResponseModal.request.id
                    ? 'Processing...'
                    : showResponseModal.action === 'approve'
                      ? 'Approve'
                      : 'Reject'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={removingMember !== null}
        onClose={() => setRemovingMember(null)}
        onAction={confirmRemoveMember}
        title="Remove Team Member"
        description={`Are you sure you want to remove ${removingMember?.name} from your team?`}
        actionText="Remove"
      />
    </div>
  );
};

export default Team;
